import type { Chain } from '@/types'
import { TESTNET_EVM_CHAIN_IDS, MAINNET_EVM_CHAIN_IDS } from '@/config/chainSets'

/**
 * Address ENCODING family. The GUARDED DUPLICATE of `AddressFamily` in the backend's
 * `apps/api/src/utils/address-validator.ts` (#459) — the two tables are compared
 * source-to-source, per chain and over the FULL semantics record, by
 * `scripts/check-address-semantics-parity.mjs`, which runs in `npm test` and as a WF-1B gate.
 *
 * It is a duplicate on purpose and under a guard, not by neglect: re-homing the registry into
 * `packages/shared` forces the `Chain`-vocabulary question (the FE `Chain` here is TEN labels;
 * `packages/shared`'s is SIX and the backend's registry has NINE), which is an excluded design
 * decision recorded on #462. Only the TABLE duplicates; nothing else about the mechanism does.
 */
export type AddressFamily = 'evm' | 'base58' | 'base58check'

/**
 * `addrType` is DERIVED from `family`, one direction, never declared beside it.
 *
 * `addrType` is the WALLET-FAMILY vocabulary — which wallet connects, which validator runs, which
 * "Connect X wallet" button renders (`ChainPickerModal`, `RecipientModal`, `isChainOnThisBuild`).
 * `family` is the ADDRESS-ENCODING vocabulary. They answer different questions and they agree today
 * only because the chain set makes them agree — which is exactly the "correct by accident of the
 * current chain set" property #459 exists to kill. Carrying both as independent per-row fields would
 * have shipped a NEW silent-drift surface inside the guard this job is building.
 *
 * The map is TOTAL over `AddressFamily`, so a new family cannot be added without deciding which
 * wallet family it belongs to, and `family` on a registry row is typed as `keyof` this map — so a
 * `bech32` entry (#374) is a COMPILE ERROR here, not a silent mis-derivation. That is the failure
 * this direction is chosen to produce.
 *
 * KNOWN LIMIT, recorded rather than left to be rediscovered: `base58check` maps to `'tron'` because
 * Tron is the only base58check chain on this build. BTC legacy P2PKH/P2SH, LTC and ZEC t-addresses
 * are also base58check, so the FIRST of them to get a `CHAIN_REGISTRY` row makes this derivation
 * wrong — it would render a "Connect Tron wallet" button for a Bitcoin address. The trigger is
 * therefore "a second base58check chain lands", and the obligation at that point is that `addrType`
 * stops being derived from `family` alone. Until then this is exact, and `chains.test.ts` pins it.
 */
const ADDR_TYPE_BY_FAMILY = {
  evm: 'evm',
  base58: 'solana',
  base58check: 'tron',
} as const satisfies Record<AddressFamily, 'evm' | 'solana' | 'tron'>

/** The per-chain address case-semantics record. Field-for-field the backend's `AddressSemantics`. */
export interface ChainAddressSemantics {
  /** The address ENCODING. */
  family: AddressFamily
  /** Is casing part of the address IDENTITY? true ⇒ folding yields a different address. */
  caseSensitive: boolean
  /** How to canonicalise for storage and comparison. `as-is` means "do not touch the bytes". */
  canonical: 'lowercase' | 'as-is'
  /** Is a MIXED-case string a valid address of this family? `false` for bech32/bech32m (BIP-173). */
  mixedCaseValid: boolean
}

export interface ChainMeta extends ChainAddressSemantics {
  name: string
  short: string
  color: string
  letter: string
  nativeCurrency: string
  /** DERIVED from `family` — see `ADDR_TYPE_BY_FAMILY`. Never written per row. */
  addrType: 'evm' | 'solana' | 'tron'
  bridgeable: boolean
  dexRoutable: boolean
}

/**
 * THE TABLE. One row per chain, every semantics field written out as a literal — the parity checker
 * reads this from SOURCE and fails CLOSED on a record it cannot extract, so a shared constant
 * referenced ten times would defeat it.
 *
 * `avalanche` is FE-ONLY (bridgeable:false, dexRoutable:false — it appears in no derived list) and
 * has no backend registry row. That asymmetry is declared to the checker as `BE_EXEMPT`; any OTHER
 * one-sided label is a red check.
 */
const CHAIN_REGISTRY_BASE: Record<Chain, Omit<ChainMeta, 'addrType'>> = {
  ethereum: { name: 'Ethereum', short: 'ETH', color: '#627EEA', letter: 'E', nativeCurrency: 'ETH', family: 'evm', caseSensitive: false, canonical: 'lowercase', mixedCaseValid: true, bridgeable: true, dexRoutable: true },
  base:     { name: 'Base',     short: 'BASE', color: '#0052FF', letter: 'B', nativeCurrency: 'ETH', family: 'evm', caseSensitive: false, canonical: 'lowercase', mixedCaseValid: true, bridgeable: true, dexRoutable: true },
  solana:   { name: 'Solana',   short: 'SOL',  color: '#9945FF', letter: 'S', nativeCurrency: 'SOL', family: 'base58', caseSensitive: true, canonical: 'as-is', mixedCaseValid: true, bridgeable: true, dexRoutable: true },
  arbitrum: { name: 'Arbitrum', short: 'ARB',  color: '#28A0F0', letter: 'A', nativeCurrency: 'ETH', family: 'evm', caseSensitive: false, canonical: 'lowercase', mixedCaseValid: true, bridgeable: true, dexRoutable: true },
  polygon:  { name: 'Polygon',  short: 'POL',  color: '#8247E5', letter: 'P', nativeCurrency: 'POL', family: 'evm', caseSensitive: false, canonical: 'lowercase', mixedCaseValid: true, bridgeable: true, dexRoutable: true },
  optimism: { name: 'Optimism', short: 'OP',   color: '#FF0420', letter: 'O', nativeCurrency: 'ETH', family: 'evm', caseSensitive: false, canonical: 'lowercase', mixedCaseValid: true, bridgeable: true, dexRoutable: true },
  avalanche:{ name: 'Avalanche',short: 'AVAX', color: '#E84142', letter: 'A', nativeCurrency: 'AVAX', family: 'evm', caseSensitive: false, canonical: 'lowercase', mixedCaseValid: true, bridgeable: false, dexRoutable: false },
  bnb:      { name: 'BNB Chain',short: 'BSC',  color: '#F0B90B', letter: 'B', nativeCurrency: 'BNB', family: 'evm', caseSensitive: false, canonical: 'lowercase', mixedCaseValid: true, bridgeable: false, dexRoutable: true },
  tron:     { name: 'Tron',    short: 'TRX',  color: '#FF0013', letter: 'T', nativeCurrency: 'TRX', family: 'base58check', caseSensitive: true, canonical: 'as-is', mixedCaseValid: true, bridgeable: false, dexRoutable: true },
  robinhood:{ name: 'Robinhood',short: 'RH',  color: '#CCFF00', letter: 'R', nativeCurrency: 'ETH', family: 'evm', caseSensitive: false, canonical: 'lowercase', mixedCaseValid: true, bridgeable: false, dexRoutable: true },
}

/**
 * `CHAIN_REGISTRY_BASE` with `addrType` derived. `Record<Chain, ChainMeta>` is TOTAL, so a `Chain`
 * label with no row is a compile error — which is the FE half of this job's OUTCOME.
 */
export const CHAIN_REGISTRY: Record<Chain, ChainMeta> = Object.fromEntries(
  (Object.keys(CHAIN_REGISTRY_BASE) as Chain[]).map(c => [
    c,
    { ...CHAIN_REGISTRY_BASE[c], addrType: ADDR_TYPE_BY_FAMILY[CHAIN_REGISTRY_BASE[c].family] },
  ]),
) as Record<Chain, ChainMeta>

/**
 * The EVM chain-id map for THIS build. Same resolution as `useDexBridge.ts:55` — one mechanism, not
 * a second one. `chainSets.ts` is THE source of truth for both networks' chain lists (it is also
 * what `config/wagmi.ts` and the #144 recipient-code sweep derive from); this module READS it and
 * never restates it.
 */
const isTestnetBuild = (import.meta.env.VITE_NETWORK || 'testnet') === 'testnet'
const ACTIVE_EVM_CHAIN_IDS = isTestnetBuild ? TESTNET_EVM_CHAIN_IDS : MAINNET_EVM_CHAIN_IDS

/**
 * I-3 (offer == resolvable): is this chain actually resolvable by the wallet layer on THIS build?
 *
 * The UI marked Robinhood dex-routable network-independently, but Robinhood (4663) exists ONLY in
 * `MAINNET_CHAINS_BY_KEY` (#174 — there is no Robinhood testnet), so on a testnet build
 * `EVM_CHAIN_IDS['robinhood']` is `undefined` and the swap send path cannot resolve a chain id. The
 * picker must therefore not OFFER it there.
 *
 * Deliberately GENERALISED, not a `robinhood` special-case: any `addrType:'evm'` chain is offerable
 * only if it is present in the active map. Non-EVM families (solana, tron) never appear in
 * `chainSets` and are unaffected.
 *
 * NOTE: this filters the DERIVED lists only. `CHAIN_REGISTRY`'s keys are NEVER removed or made
 * conditional — nine call sites index it non-optionally (RecipientModal, ReviewTransferModal,
 * TokenPill, ChainIcon, ChainPickerModal, tokens.ts) and a missing key would throw a white screen.
 */
export const isChainOnThisBuild = (c: Chain): boolean =>
  CHAIN_REGISTRY[c].addrType !== 'evm' || c in ACTIVE_EVM_CHAIN_IDS

/**
 * Chains with deployed CCTP contracts — used by chain picker modal.
 * Deliberately NOT filtered by `isChainOnThisBuild`: every bridgeable chain exists on both networks,
 * so filtering here is a no-op today and would only widen this change's blast radius.
 */
export const BRIDGE_CHAINS = (Object.keys(CHAIN_REGISTRY) as Chain[]).filter(
  c => CHAIN_REGISTRY[c].bridgeable
)

/** Chains available for DEX swaps — empty for now, populated in Sessions 39-40 */
export const DEX_CHAINS = (Object.keys(CHAIN_REGISTRY) as Chain[]).filter(
  c => CHAIN_REGISTRY[c].dexRoutable && isChainOnThisBuild(c)
)

/** EVM chains available as destination — BRIDGE_CHAINS ∪ DEX_CHAINS filtered to addrType === 'evm' */
export const EVM_DEST_CHAINS = (Object.keys(CHAIN_REGISTRY) as Chain[]).filter(
  c => (CHAIN_REGISTRY[c].bridgeable || CHAIN_REGISTRY[c].dexRoutable) && CHAIN_REGISTRY[c].addrType === 'evm' && isChainOnThisBuild(c)
)

/** All chains available as source or dest — bridgeable ∪ dexRoutable (all families) */
export const ALL_CHAINS = (Object.keys(CHAIN_REGISTRY) as Chain[]).filter(
  c => (CHAIN_REGISTRY[c].bridgeable || CHAIN_REGISTRY[c].dexRoutable) && isChainOnThisBuild(c)
)
